"use client";

import { useEffect, useState } from "react";
import { fetchWeather, getDemoWeather } from "@/lib/weather";
import type { WeatherData } from "@/lib/weather";
import LoadingSpinner from "@/app/components/atoms/LoadingSpinner";

/**
 * @component WeatherCard
 * @description Displays the current temperature at Grounds For Sculpture.
 * Fetches live data from the Open-Meteo API and falls back to demo data on error.
 * @returns A card showing the current temperature and a weather icon, or a loading placeholder.
 */
export default function WeatherCard() {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadWeather = async () => {
      try {
        const data = await fetchWeather();
        if (isMounted) setWeather(data);
      } catch (error) {
        console.error("Error loading weather:", error);
        if (isMounted) setWeather(getDemoWeather());
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadWeather();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div
      className="bg-canvas p-6 flex flex-col justify-between min-h-[180px] relative w-full"
      aria-live="polite"
    >
      <div>
        <h2 className="text-title text-charcoal mb-4">Weather at GFS</h2>
        {loading ? (
          <LoadingSpinner />
        ) : weather ? (
          <div className="flex items-center gap-4">
            <span className="text-stat text-charcoal">
              {weather.temp}°
              <span className="sr-only">
                {" "}
                degrees Fahrenheit — current temperature at Grounds For
                Sculpture
              </span>
            </span>
            <span className="text-subheading text-stone" aria-hidden>
              F
            </span>
          </div>
        ) : null}
      </div>
      {weather && (
        <div className="absolute bottom-6 right-6 text-5xl" aria-hidden>
          {weather.icon}
        </div>
      )}
    </div>
  );
}
